import { v4 as uuidv4 } from "uuid";
import { constructPathURL } from "../utils/constructPathURL.js";
import {
    CHAT_SESSION_ID,
    SOURCE_CHAT,
    IS_LOGIN,
    KEYS,
    ERROR_MSG,
    CLIENT_SECRET,
    FIVE_MIN,
} from "../../constants.js";
import { formatMessageData, formatMessageDataV2 } from "../utils/formatData.js";
import {
    concatMessage,
    setLoadingReply,
    setMessages,
} from "../features/messagesSlice.js";
import store from "../app/store.js";
import { resetSelfService, setSelfService } from "../features/sessionSlice.js";
import intentCopywriting from "../utils/intentCopywriting.js";
import { setIsTypingContainerState } from "../features/componentStateSlice.js";
import { getDecryptedStorage } from "../utils/crypto.js";

// Hardcoded client id (not read from index.html)
const CLIENT_ID = "3f4e2a1b-9c7d-4e28-b6f5-2d1a3e4b5c6d";

export async function requestChat() {
    const requestChatUrl = constructPathURL("/init");
    const deviceId = uuidv4();
    const timeNow = new Date();
    const expiredAt = new Date(timeNow.getTime() + 6 * 60000);

    const requestData = {
        channel: SOURCE_CHAT,
        deviceId: deviceId,
        clientId: CLIENT_ID,
    };

    const response = await fetch(requestChatUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
    });

    if (!response.ok) {
        throw new Error("Request response was not ok");
    }

    const responseData = await response.json();

    if (responseData.code !== "0000") {
        throw new Error(
            "Request response was not success",
            responseData.message
        );
    } else {
        return { ...responseData, deviceId: requestData.deviceId, expiredAt };
    }
}

export async function getToken(correlationId) {
    const tokenURL = constructPathURL("/auth");

    const tokenData = {
        client_id: CLIENT_ID,
        channel: SOURCE_CHAT,
    };

    const response = await fetch(tokenURL, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-correlation-id": correlationId,
        },
        body: JSON.stringify(tokenData),
    });

    if (!response.ok) {
        if (response.status === 401) {
            await requestChat();
        } else {
            throw new Error("Generate token response was not ok");
        }
    }
    return response.json();
}

export async function sendChat(
    deviceId,
    token,
    sKey,
    content,
    position,
    dispatch
) {
    dispatch(setIsTypingContainerState(true));
    const sendChatUrl = constructPathURL("/api/v3/chat");

    const sendData = {
        content: typeof content === "string" ? content : content.accountNumber,
        channel: SOURCE_CHAT,
        chat_id: CHAT_SESSION_ID,
        chat_type: "text",
        request_id: uuidv4(),
        self_service: store.getState().session.selfService,
        selected: typeof content === "string" ? {} : content,
    };

    try {
        const sendResponse = await fetch(sendChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(sendData),
        });

        if (!sendResponse.ok) {
            // Regenerate token if token invalid
            if (sendResponse.status === 401) {
                const newRequestResponse = await requestChat();
                const newTokenResponse = await getToken(
                    newRequestResponse.correlation_id
                );
                await sendChat(
                    deviceId,
                    newTokenResponse.token,
                    newTokenResponse.s_key,
                    content,
                    position,
                    dispatch
                );
            } else {
                dispatch(setIsTypingContainerState(false));
                const botReply = formatMessageDataV2(
                    deviceId,
                    "bot",
                    ERROR_MSG,
                    position,
                    true
                );
                dispatch(concatMessage(botReply));
                //* Commented code below because want to keep conversation alive
                // throw new Error("Send response was not ok");
            }
        }

        const cloned = sendResponse.clone();
        const reader = sendResponse.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let result = "";
        let token_position = 0;
        let isStreamed = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (IS_LOGIN) {
                    if (!isStreamed) {
                        const clonedResponse = await cloned.json();
                        if (clonedResponse.data.selection) {
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                "Please select an account number.",
                                position
                            );
                            dispatch(concatMessage(botReply));
                            clonedResponse.data.selection.push({
                                productName:
                                    "Not seeing your card? Click here.",
                                accountNumber: "not_found",
                            });
                            const selectionMessage = formatMessageData(
                                deviceId,
                                "bot",
                                clonedResponse.data.selection,
                                position,
                                "selection"
                            );
                            dispatch(concatMessage(selectionMessage));
                            dispatch(
                                setSelfService(clonedResponse.data.self_service)
                            );
                            dispatch(setLoadingReply(true));
                        } else if (clonedResponse.data.data.length !== 0) {
                            const intentResponse = await intentCopywriting(
                                clonedResponse.data.self_service.intent,
                                clonedResponse.data.data,
                                content
                            );
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                intentResponse,
                                position
                            );
                            dispatch(concatMessage(botReply));
                            dispatch(resetSelfService());
                        }
                    }
                }
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const chunk_obj = chunk.split("\n");

            dispatch(setIsTypingContainerState(false));

            try {
                const jsonObjects = chunk_obj
                    .filter((item) => item != "")
                    .map((item) => JSON.parse(item));

                jsonObjects.forEach((jsonObject) => {
                    if (jsonObject.done) {
                        dispatch(setLoadingReply(false));
                        return;
                    }

                    const currentMessages = store.getState().messages.messages;
                    let updatedMessages = [...currentMessages];

                    if (token_position == 0) {
                        const tempMessage = formatMessageData(
                            jsonObject.conversation_id,
                            "bot",
                            jsonObject.response,
                            position
                        );
                        updatedMessages.push(tempMessage);
                    } else {
                        const latestMessage = {
                            ...updatedMessages[updatedMessages.length - 1],
                        };
                        latestMessage.content += jsonObject.response;
                        updatedMessages[updatedMessages.length - 1] =
                            latestMessage;
                    }
                    token_position++;
                    dispatch(setMessages(updatedMessages));
                    dispatch(resetSelfService());

                    if (jsonObject.response) {
                        result += jsonObject.response;
                        isStreamed = true;
                    }
                });
            } catch (e) {
                console.error("Error parsing JSON chunk:", e);
            }
        }
        return result;
    } catch (error) {
        throw new Error("Send response was not success", error);
    }
}

export async function sendChatPrelogin(
    deviceId,
    token,
    sKey,
    content,
    position,
    dispatch
) {
    dispatch(setIsTypingContainerState(true));
    const sendChatUrl = constructPathURL("/api/chat");

    const sendData = {
        content: typeof content === "string" ? content : content.accountNumber,
        channel: SOURCE_CHAT,
        chat_id: CHAT_SESSION_ID,
        chat_type: "text",
        request_id: uuidv4(),
        self_service: store.getState().session.selfService,
        selected: typeof content === "string" ? {} : content,
    };

    try {
        const sendResponse = await fetch(sendChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(sendData),
        });

        if (!sendResponse.ok) {
            // Regenerate token if token invalid
            if (sendResponse.status === 401) {
                const newRequestResponse = await requestChat();
                const newTokenResponse = await getToken(
                    newRequestResponse.correlation_id
                );
                await sendChatPrelogin(
                    deviceId,
                    newTokenResponse.token,
                    newTokenResponse.s_key,
                    content,
                    position,
                    dispatch
                );
            } else {
                dispatch(setIsTypingContainerState(false));
                const botReply = formatMessageDataV2(
                    deviceId,
                    "bot",
                    ERROR_MSG,
                    position,
                    true
                );
                dispatch(concatMessage(botReply));

                //* Commented code below because want to keep conversation alive
                // throw new Error("Send response was not ok");
            }
        }

        const cloned = sendResponse.clone();
        const reader = sendResponse.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let result = "";
        let token_position = 0;
        let isStreamed = false;

        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                if (IS_LOGIN) {
                    if (!isStreamed) {
                        const clonedResponse = await cloned.json();
                        if (clonedResponse.data.selection) {
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                "Please select an account number.",
                                position
                            );
                            dispatch(concatMessage(botReply));
                            clonedResponse.data.selection.push({
                                productName:
                                    "Not seeing your card? Click here.",
                                accountNumber: "not_found",
                            });
                            const selectionMessage = formatMessageData(
                                deviceId,
                                "bot",
                                clonedResponse.data.selection,
                                position,
                                "selection"
                            );
                            dispatch(concatMessage(selectionMessage));
                            dispatch(
                                setSelfService(clonedResponse.data.self_service)
                            );
                            dispatch(setLoadingReply(true));
                        } else if (clonedResponse.data.data.length !== 0) {
                            const intentResponse = await intentCopywriting(
                                clonedResponse.data.self_service.intent,
                                clonedResponse.data.data,
                                content
                            );
                            const botReply = formatMessageData(
                                deviceId,
                                "bot",
                                intentResponse,
                                position
                            );
                            dispatch(concatMessage(botReply));
                            dispatch(resetSelfService());
                        }
                    }
                }
                break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const chunk_obj = chunk.split("\n");

            // Seems like status response come first, then take sometimes to get content response (API)
            dispatch(setIsTypingContainerState(false));

            try {
                const jsonObjects = chunk_obj
                    .filter((item) => item != "")
                    .map((item) => JSON.parse(item));

                jsonObjects.forEach((jsonObject) => {
                    if (jsonObject.done) {
                        dispatch(setLoadingReply(false));
                        return;
                    }

                    const currentMessages = store.getState().messages.messages;
                    let updatedMessages = [...currentMessages];

                    if (token_position == 0) {
                        const tempMessage = formatMessageData(
                            jsonObject.conversation_id,
                            "bot",
                            jsonObject.response,
                            position
                        );
                        updatedMessages.push(tempMessage);
                    } else {
                        const latestMessage = {
                            ...updatedMessages[updatedMessages.length - 1],
                        };
                        latestMessage.content += jsonObject.response;
                        updatedMessages[updatedMessages.length - 1] =
                            latestMessage;
                    }

                    token_position++;
                    dispatch(setMessages(updatedMessages));
                    dispatch(resetSelfService());

                    if (jsonObject.response) {
                        result += jsonObject.response;
                        isStreamed = true;
                    }
                });
            } catch (e) {
                console.error("Error parsing JSON chunk:", e);
            }
        }
        return result;
    } catch (error) {
        throw new Error("Send response was not success", error);
    }
}

export async function endChat(token) {
    const endChatUrl = constructPathURL("/api/logout");

    const response = await fetch(endChatUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error("End chat was not successful");
    }

    return response.json();
}

export const createHistoryChat = async (
    token,
    question,
    answer,
    prelogin_session,
    chat_type = "ai_assistance",
    request_id = "3432432432423"
) => {
    const createHistoryChatUrl = constructPathURL("/api/chat-history");

    const createHistoryChatData = JSON.stringify({
        question,
        answer,
        chat_type,
        request_id,
        prelogin_session,
    });

    try {
        const res = await fetch(createHistoryChatUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: createHistoryChatData,
        });

        return res;
    } catch (error) {
        console.log("error", error);
    }
};

export const getHistoryChat = async (token) => {
    const getHistoryChatUrl = constructPathURL("/api/chat-history");

    try {
        const res = await fetch(getHistoryChatUrl, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        return res.json();
    } catch (error) {
        console.log("error", error);
    }
};

export const rateConversation = async (token, type, id) => {
    const apiUrl = constructPathURL("/api/rating");

    const payload = {
        conversation_id: id,
        thumbs_up: type,
    };

    try {
        const res = await fetch(apiUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        await res.json();
    } catch (error) {
        console.log(error);
    }
};
